import cn from "@/v2-ui/utils/utils.cn"
import type { PublicServiceDetail } from "@/v2-status-client/status/status.types"
import StatusDot from "@/v2-status-client/status/dot/StatusDot"
import StatusHistoryBar from "@/v2-status-client/status/history/StatusHistoryBar"
import StatusSparkline from "@/v2-status-client/status/sparkline/StatusSparkline"
import {
  formatLatencyMs,
  formatUptimePercent,
} from "@/v2-status-client/status/status.utils"

export type StatusCategoryServiceItemProps = {
  service: PublicServiceDetail
}

// One row inside a category's expanded service list. Four blocks:
//   - identity (dot + name + host + provider + summary)
//   - latency stats (avg / p95)
//   - 30-day daily up/down strip + 30d uptime % label
//   - 24h hourly latency sparkline
//
// The 30-day strip used to live on the parent category / platform
// row; we moved it onto each service so a customer scanning a long
// list can see history right next to the service that's actually
// degraded instead of mentally backing up to the parent card.
//
// Layout collapses to a single column on narrow viewports; the
// strip and sparkline are flex blocks so the columns don't reflow
// as values change between polls.
function StatusCategoryServiceItem(
  props: Readonly<StatusCategoryServiceItemProps>,
) {
  const { service } = props

  return (
    <li
      className={cn(
        "grid grid-cols-12 items-center gap-x-3 gap-y-1 border-b border-border/30 py-2 text-sm last:border-b-0",
      )}
    >
      <div className="col-span-12 sm:col-span-4 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot status={service.status} />
          <span className="truncate font-medium text-foreground">
            {service.name}
          </span>
        </div>
        <div className="mt-0.5 ml-4 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          {service.host && <span className="truncate">{service.host}</span>}
          {service.host && service.providerName && <span aria-hidden>·</span>}
          {service.providerName && <span>{service.providerName}</span>}
          {(service.host || service.providerName) && <span aria-hidden>·</span>}
          <span>{service.summary}</span>
        </div>
      </div>

      <div className="col-span-12 sm:col-span-4 flex flex-col items-stretch -mb-2">
        <StatusHistoryBar history={service.history} />
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground/80">
          <span>30 days</span>
          <span className="font-mono tabular-nums text-foreground/80">
            {formatUptimePercent(service.uptimePercent30d)}
          </span>
        </div>
      </div>

      <div className="col-span-6 sm:col-span-2 flex justify-center text-xs">
        <div className="grid grid-cols-[auto_auto] gap-x-2 gap-y-0 leading-tight">
          <span className="text-muted-foreground">Avg</span>
          <span className="text-right font-mono tabular-nums text-foreground/90">
            {formatLatencyMs(service.avgDurationMs)}
          </span>
          <span className="text-muted-foreground">p95</span>
          <span className="text-right font-mono tabular-nums text-foreground/90">
            {formatLatencyMs(service.p95DurationMs)}
          </span>
        </div>
      </div>

      <div className="col-span-6 sm:col-span-2 flex items-center justify-end">
        <StatusSparkline
          values={service.latencySparkline}
          status={service.status}
          baselineMs={service.p95DurationMs}
          title={`${service.name} · last 24h latency`}
        />
      </div>
    </li>
  )
}

export default StatusCategoryServiceItem
